import React, { useState, useCallback, useEffect } from 'react';
import { useApi } from '@/shared/hooks/useApi';
import applicationService from '@/services/api/applications';
import { Application, ApprovalStatus, Location } from '@/types/models/schema';
import { FilterOptions, ApiResponse } from '@/types/api';
import { AxiosInstance } from 'axios';

// Define the application input type to match schema requirements
export interface ApplicationInput {
  applicant_name: string;
  applicant_email: string;
  company?: string | undefined;
  title?: string | undefined;
  bio?: string | undefined;
  location?: Location | undefined;
  status: ApprovalStatus;
}

/**
 * Simplified useApplications Hook
 * 
 * Provides data and operations for managing podcast guest applications
 */
export function useApplications(initialFilters?: FilterOptions) {
  const [filters, setFilters] = useState<FilterOptions>(initialFilters || {});
  
  // Use the updated useApi hook pattern with correct signature
  const {
    data: applications,
    isLoading,
    error,
    refetch,
    retry
  } = useApi<Application[]>(
    useCallback(async (client: AxiosInstance) => {
      return applicationService.getAll(filters);
    }, [filters])
  );
  
  const fetchApplications = useCallback(async (filterOptions?: FilterOptions) => {
    if (filterOptions) {
      setFilters(filterOptions);
    }
    return refetch();
  }, [refetch]);
  
  const getApplicationById = useCallback(async (id: string): Promise<ApiResponse<Application>> => {
    const rawResponse = await applicationService.getById(id);
    
    // Wrap raw response in ApiResponse
    const response: ApiResponse<Application> = {
      data: rawResponse,
      error: rawResponse === null ? new Error(`Failed to fetch application with ID ${id}`) : null
    };
    
    return response;
  }, []);
  
  const createApplication = useCallback(async (data: ApplicationInput): Promise<ApiResponse<Application>> => {
    const rawResponse = await applicationService.create(data);
    refetch();
    
    // Wrap raw response in ApiResponse
    const response: ApiResponse<Application> = {
      data: rawResponse,
      error: rawResponse === null ? new Error('Failed to create application') : null
    };
    
    return response;
  }, [refetch]);
  
  const updateApplication = useCallback(async (id: string, data: Partial<ApplicationInput>): Promise<ApiResponse<Application>> => {
    const rawResponse = await applicationService.update(id, data);
    refetch();
    
    // Wrap raw response in ApiResponse
    const response: ApiResponse<Application> = {
      data: rawResponse,
      error: rawResponse === null ? new Error(`Failed to update application with ID ${id}`) : null
    };
    
    return response;
  }, [refetch]);
  
  const deleteApplication = useCallback(async (id: string): Promise<ApiResponse<boolean>> => {
    const rawResponse = await applicationService.delete(id);
    refetch();
    
    // Wrap raw response in ApiResponse
    const response: ApiResponse<boolean> = {
      data: rawResponse,
      error: rawResponse === false ? new Error(`Failed to delete application with ID ${id}`) : null
    };
    
    return response;
  }, [refetch]);
  
  const convertToGuest = useCallback(async (id: string): Promise<ApiResponse<boolean>> => {
    const rawResponse = await applicationService.convertToGuest(id);
    refetch();
    
    // Wrap raw response in ApiResponse
    const response: ApiResponse<boolean> = {
      data: rawResponse,
      error: rawResponse === false ? new Error(`Failed to convert application with ID ${id} to guest`) : null
    };
    
    return response;
  }, [refetch]);
  
  // Load applications on mount and when filters change
  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);
  
  return {
    applications,
    isLoading,
    error,
    filters,
    setFilters,
    fetchApplications,
    getApplicationById,
    createApplication,
    updateApplication,
    deleteApplication,
    convertToGuest,
    refetchApplications: refetch,
    retryFetch: retry
  };
}

export default useApplications;
